import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useI18n } from '../lib/i18n';
import PasswordRules, { passwordOk } from './PasswordRules';
import PasswordInput from './PasswordInput';
import BackupCard from './BackupCard';

const TX = {
  sr: { current: 'Trenutna lozinka', next: 'Nova lozinka', repeat: 'Ponovite novu lozinku', wrongOld: 'Trenutna lozinka nije tačna.', mismatch: 'Nove lozinke se ne poklapaju.', same: 'Nova lozinka mora biti drugačija od trenutne.', rules: 'Nova lozinka ne ispunjava pravila.', fillOld: 'Unesite trenutnu lozinku.' },
  en: { current: 'Current password', next: 'New password', repeat: 'Repeat new password', wrongOld: 'Current password is incorrect.', mismatch: 'New passwords do not match.', same: 'New password must differ from the current one.', rules: 'New password does not meet the rules.', fillOld: 'Enter your current password.' },
  ru: { current: 'Текущий пароль', next: 'Новый пароль', repeat: 'Повторите новый пароль', wrongOld: 'Текущий пароль неверный.', mismatch: 'Новые пароли не совпадают.', same: 'Новый пароль должен отличаться от текущего.', rules: 'Новый пароль не соответствует правилам.', fillOld: 'Введите текущий пароль.' },
};

export default function UserProfile() {
  const { user } = useAuth();
  const { lang, setLang, t } = useI18n();
  const tx = TX[lang] || TX.sr;
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [repeatPw, setRepeatPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const mismatch = repeatPw.length > 0 && newPw !== repeatPw;
  const canSave = oldPw && passwordOk(newPw) && newPw === repeatPw && !saving;

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!oldPw) { setError(tx.fillOld); return; }
    if (!passwordOk(newPw)) { setError(tx.rules); return; }
    if (newPw !== repeatPw) { setError(tx.mismatch); return; }
    if (newPw === oldPw) { setError(tx.same); return; }
    setSaving(true);
    // Provera trenutne lozinke: ponovna prijava istim emailom i starom lozinkom
    const { error: authErr } = await supabase.auth.signInWithPassword({ email: user.email, password: oldPw });
    if (authErr) { setSaving(false); setError(tx.wrongOld); return; }
    const { error: updErr } = await supabase.auth.updateUser({ password: newPw });
    setSaving(false);
    if (updErr) { setError(`${t('passwordChangeError')} (${updErr.message})`); return; }
    setInfo(t('passwordChanged'));
    setOldPw(''); setNewPw(''); setRepeatPw('');
  };

  // Naziv svakog jezika na samom tom jeziku, da ga korisnik uvek prepozna
  const LANGS = [['sr', 'Srpski'], ['en', 'English'], ['ru', 'Русский']];

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h1 style={styles.heading}>{t('userProfile')}</h1>

      <div style={styles.card}>
        <div style={styles.label}>{t('emailAddress')}</div>
        <div style={styles.value}>{user?.email}</div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('appLanguage')}</div>
        <div style={styles.langRow}>
          {LANGS.map(([code, label]) => (
            <button key={code} onClick={() => setLang(code)} style={{ ...styles.langBtn, ...(lang === code ? styles.langBtnActive : {}) }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('changePassword')}</div>
        <form onSubmit={handlePasswordChange}>
          <label style={styles.fieldLabel}>{tx.current}</label>
          <PasswordInput value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" ariaLabel={tx.current} />

          <label style={styles.fieldLabel}>{tx.next}</label>
          <PasswordInput value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" ariaLabel={tx.next} />
          <PasswordRules password={newPw} />

          <label style={styles.fieldLabel}>{tx.repeat}</label>
          <PasswordInput value={repeatPw} onChange={(e) => setRepeatPw(e.target.value)} autoComplete="new-password" ariaLabel={tx.repeat} invalid={mismatch} />
          {mismatch && <div style={styles.fieldError}>{tx.mismatch}</div>}

          {error && <div style={styles.errorBox}>{error}</div>}
          {info && <div style={styles.info}>{info}</div>}
          <button type="submit" disabled={!canSave} style={{ ...styles.saveBtn, opacity: canSave ? 1 : 0.5 }}>
            {saving ? t('savingEllipsisShort') : t('changePasswordBtn')}
          </button>
        </form>
      </div>

      <BackupCard />
    </div>
  );
}

const styles = {
  heading: { fontSize: 22, marginBottom: 16 },
  card: { background: 'var(--surface)', borderRadius: 10, padding: 18, marginBottom: 14, border: '1px solid var(--line)' },
  label: { fontSize: 12, color: 'var(--ink-500)', marginBottom: 4 },
  value: { fontSize: 15, fontWeight: 600, color: 'var(--ink-900)' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: 'var(--teal-800)', marginBottom: 12 },
  fieldLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5 },
  langRow: { display: 'flex', gap: 8 },
  langBtn: { flex: 1, border: '1.5px solid var(--line)', background: 'var(--surface)', borderRadius: 10, padding: '10px 0', fontWeight: 600, fontSize: 13, color: 'var(--ink-700)' },
  langBtnActive: { background: 'var(--teal-800)', color: '#fff', borderColor: 'var(--teal-800)' },
  fieldError: { fontSize: 12, color: 'var(--red-600)', marginTop: -6, marginBottom: 12 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  info: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  saveBtn: { minHeight: 34, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13 },
};
